import { useMemo, useState } from "react";
import { categories, verifyItems, type VerifyStatus } from "@/mocks/verify";

const statusMeta: Record<
  VerifyStatus,
  { label: string; pill: string; dot: string }
> = {
  confirmed: { label: "证据坐实", pill: "bg-accent-100 text-accent-800", dot: "bg-accent-500" },
  refuted: { label: "查无实据", pill: "bg-secondary-200 text-secondary-900", dot: "bg-secondary-500" },
  partial: { label: "部分属实", pill: "bg-primary-100 text-primary-800", dot: "bg-primary-500" },
  reversed: { label: "经历反转", pill: "bg-accent-200 text-accent-900", dot: "bg-accent-600" },
  reviewing: { label: "核实中", pill: "bg-background-200 text-foreground-700", dot: "bg-foreground-400" },
};

export default function LatestFeed() {
  const [active, setActive] = useState("全部栏目");
  const [expanded, setExpanded] = useState<number | null>(null);

  const list = useMemo(
    () =>
      active === "全部栏目"
        ? verifyItems
        : verifyItems.filter((it) => it.category === active),
    [active]
  );

  return (
    <section id="latest" className="border-y border-background-200 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-600">
              Latest Verification
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-[2rem]">
              最新求证结果
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              每一条传闻都经过信息源、证据链与时间线三步核验。点击卡片可展开证据要点，判断依据一律公开可查。
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(["confirmed", "refuted", "partial", "reversed", "reviewing"] as VerifyStatus[]).map(
              (k) => (
                <span
                  key={k}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${statusMeta[k].pill}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${statusMeta[k].dot}`}></span>
                  {statusMeta[k].label}
                </span>
              )
            )}
          </div>
        </div>

        {/* 栏目筛选 */}
        <div className="mt-8 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setActive(c);
                setExpanded(null);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                active === c
                  ? "bg-primary-600 text-background-50"
                  : "border border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* 卡片流 */}
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {list.map((it) => {
            const meta = statusMeta[it.status];
            const isOpen = expanded === it.id;
            return (
              <article
                key={it.id}
                className="flex flex-col rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                    <i className="ri-focus-3-line text-xs" aria-hidden="true"></i>
                    {it.category}
                  </span>
                  <span className="text-xs text-foreground-400">{it.date}</span>
                </div>

                <h3 className="mt-4 font-heading text-lg leading-snug font-bold text-foreground-950">
                  {it.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">{it.summary}</p>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {it.tags.map((t) => (
                    <span
                      key={t}
                      className="whitespace-nowrap rounded-md bg-background-100 px-2 py-0.5 text-[11px] font-medium text-foreground-500"
                    >
                      #{t}
                    </span>
                  ))}
                </div>

                {isOpen && (
                  <ul className="mt-4 space-y-2 rounded-lg border border-background-200 bg-background-100 p-4">
                    {it.verdict.map((v, i) => (
                      <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-foreground-700">
                        <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-primary-600" aria-hidden="true"></i>
                        {v}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-5 flex items-center justify-between border-t border-background-200 pt-4">
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${meta.pill}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`}></span>
                    {meta.label}
                  </span>
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : it.id)}
                    className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800"
                    aria-expanded={isOpen}
                  >
                    {isOpen ? "收起依据" : "查看求证依据"}
                    <i className={`${isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base`} aria-hidden="true"></i>
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {list.length === 0 && (
          <div className="mt-8 flex flex-col items-center gap-2 rounded-xl border border-background-200 bg-background-50 py-16 text-center">
            <i className="ri-inbox-line text-3xl text-foreground-300" aria-hidden="true"></i>
            <p className="text-sm text-foreground-500">该栏目暂无可展示的求证样本，欢迎提交你关注的传闻。</p>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-foreground-400">
          以上为栏目展示样本，结论均附公开可查的证据要点；真实平台内将持续更新你关注的争议事件。
        </p>
      </div>
    </section>
  );
}